import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema } from "./_envelope";

export const checkoutViewed = defineEvent({
  eventName: "checkout_viewed",
  eventVersion: 2,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      source: z.string().optional(),
      product: z.string().optional(),
      billing_period: z.string().optional(),
      trial_days: z.union([z.string(), z.number()]).optional(),
      journey_id: z.string().optional(),
      n_children: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export const checkoutMethodLoaded = defineEvent({
  eventName: "checkout_method_loaded",
  eventVersion: 1,
  valueSchema: z.enum(["express", "embedded"]),
  metadataSchema: baseEnvelopeSchema
    .extend({
      load_count: z.union([z.string(), z.number()]).optional(),
      trigger: z.enum(["initial", "cadence_switch"]).optional(),
      // Full page URL (window.location.href) the checkout loaded on, incl.
      // query params (flagCode/variant). The wrapper also stamps `route`
      // (pathname); this is the complete URL.
      page_url: z.string().optional(),
      journey_id: z.string().optional(),
    })
    .passthrough(),
});

export const checkoutAbandoned = defineEvent({
  eventName: "checkout_abandoned",
  eventVersion: 1,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      time_on_checkout_ms: z.union([z.string(), z.number()]).optional(),
      last_field_touched: z.string().optional(),
      cadence_switched: z.union([z.string(), z.boolean()]).optional(),
      source: z.string().optional(),
      journey_id: z.string().optional(),
    })
    .passthrough(),
});

/**
 * Fires the moment the user commits to paying — after form validation and
 * server-side subscription creation, *before* `stripe.confirmPayment` /
 * `stripe.confirmSetup`. Captures intent, regardless of whether the card
 * actually clears. Pair with `payment_succeeded` to measure card-success
 * rate as `payment_succeeded / payment_submitted`. Used by
 * `payment/page.tsx` (via `statsig:payment_submitted` window event) to
 * gate the unmount-time `checkout_abandoned` event.
 */
export const paymentSubmitted = defineEvent({
  eventName: "payment_submitted",
  eventVersion: 2,
  valueSchema: z.string(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      product_key: z.string().optional(),
      billing_period: z.string().optional(),
      flow: z.string().optional(),
      customer_email: z.string().optional(),
      paywall_variant: z.string().optional(),
      journey_id: z.string().optional(),
      trial_days: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

/**
 * Fires when the client confirms Stripe accepted the payment (post-
 * `confirmPayment` / `confirmSetup`, before the parent navigates to the
 * success page). Distinct from `payment_submitted` (intent) so the funnel
 * can split intent-to-pay from successful-charge-on-client. Server-side
 * truth lives on the webhook's `subscription_created`; this event fills
 * the gap when the webhook is delayed or the dashboard wants a
 * client-only success metric.
 */
export const paymentSucceeded = defineEvent({
  eventName: "payment_succeeded",
  eventVersion: 1,
  valueSchema: z.string(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      product_key: z.string().optional(),
      billing_period: z.string().optional(),
      flow: z.string().optional(),
      customer_email: z.string().optional(),
      paywall_variant: z.string().optional(),
      journey_id: z.string().optional(),
      trial_days: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});
